import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useQueryParam, StringParam } from "use-query-params";
import { shopActions } from "../../../actions";
import { DataShop, ProductShop, ProductShopWithVariants } from "../../../types";
import "./LandingPagePreview.css";
import LandingPagePreviewWithVariants from "./LandingPagePreviewWithVariants";

const enDict = {
  full_name: "Full name",
  phone_number: "Phone number",
  wilaya: "Wilaya",
  commune: "Commune",
  options: "Options",
  frequently_bought_together: "Frequently bought together",
  specifications: "Specifications",
  how_to_use_guide: "How-to use guide",
  more_products_from: "More products from",
  success: "Success !",
  success_message: "You have successfully added a new order with",
  buy: "Buy now",
  cancel: "Cancel",
  buy_bundle: "Buy bundle",
  total_price: "Total price",
  quantity: "Quantity:",
  success_title: "Order added successfully",
  option_wilaya: "Wilaya",
  option_commune: "Commune",
  loading: "Loading...",
  placeholder_name: "Full name",
  placeholder_phone: "Phone number",
  order_summary: "Order Summary",
  subtotal: "Subtotal",
  shipping: "Shipping",
  total_amount: "Total amount",
  description: "Description",
};
const arDict = {
  full_name: "الاسم و اللقب",
  phone_number: "رقم الهاتف",
  wilaya: "الولاية",
  commune: "البلدية",
  options: "خيارات",
  frequently_bought_together: "كثيرا ما يتم شراؤها معًا",
  specifications: "المميزات",
  how_to_use_guide: "دليل طريقة الاستخدام",
  more_products_from: "المزيد من المنتجات من",
  success: "تهانينا",
  success_message: "لقد نجحت في إضافة طلب جديد مع",
  buy: "اشتري",
  cancel: "إلغاء",
  buy_bundle: "شراء حزمة",
  total_price: "السعر الكلي",
  quantity: "الكمية:",
  success_title: "تمت إضافة الطلب بنجاح",
  option_wilaya: "الولاية",
  option_commune: "البلدية",
  loading: "تحميل...",
  placeholder_name: "الاسم",
  placeholder_phone: "رقم الهاتف",
  order_summary: "ملخص الطلب",
  subtotal: "سعر الطلب",
  shipping: "سعر التوصيل",
  total_amount: "المجموع",
  description: "وصف المنتوج",
};
const frDict = {
  full_name: "Nom et prénom",
  phone_number: "Numéro de téléphone",
  wilaya: "Wilaya",
  commune: "Commune",
  options: "Options",
  frequently_bought_together: "Produits fréquemment achetés ensemble",
  specifications: "Spécifications",
  how_to_use_guide: "Guide d'utilisation",
  more_products_from: "Plus de produits de",
  success: "Félicitations !",
  success_message: "Vous avez ajouté avec succès une nouvelle commande avec",
  buy: "Acheter",
  cancel: "Annuler",
  buy_bundle: "Acheter pack",
  total_price: "Prix total",
  quantity: "Quantité:",
  success_title: "Commande ajoutée avec succès",
  option_wilaya: "Wilaya",
  option_commune: "Commune",
  loading: "Chargement...",
  placeholder_name: "Nom complet",
  placeholder_phone: "Numéro de téléphone",
  order_summary: "Récapitulatif de la commande",
  subtotal: "Prix de la commande",
  shipping: "Prix de la livraison",
  total_amount: "Montant total",
  description: "Description",
};

const LandingPagePreview = ({
  GetLandingPageInfo,
  match,
  dataShop,
}: {
  GetLandingPageInfo: () => void;
  match: any;
  dataShop: DataShop;
}) => {
  const [SESSION_ID] = useQueryParam("sessionId", StringParam);
  const [product, setProduct] = useState<ProductShop | ProductShopWithVariants | null>(null);
  const [currency, setCurrency] = useState<string | null>(null);
  const [activeImage, setActiveImage] = useState(0);
  const [isVariants, setIsVariants] = useState(false)
  const [variantsData, setVariantsData] = useState<string>("")

  useEffect(() => {
    document.querySelector("html")?.classList.add("preview");
    GetLandingPageInfo();
  }, []);

  useEffect(() => {
    const data = localStorage.getItem(`landing-page-${SESSION_ID}`);
    if (data) {
      const payload = JSON.parse(data);
      setProduct(payload.product);
      setCurrency(payload.currency);
      if (payload.product.is_variant) {
        setIsVariants(payload.product.is_variant)
        setVariantsData(data)   
      }
    } else {
      window.open("about:blank", "_self");
      window.close();
    }
    return localStorage.removeItem(`landing-page-${SESSION_ID}`);
  }, []);

  useEffect(() => {
    const html = document.querySelector("html");
    const body = document.querySelector("body");
    if (product && dataShop) translatePage(product.lang);
    if (dataShop) {
      html?.setAttribute("theme", dataShop.theme_template);

      const box_bg = dataShop.landingPageSettings?.custom_colors?.box_bg;
      const btn_font_color =
        dataShop.landingPageSettings?.custom_colors?.btn_font_color;
      const button_bg = dataShop.landingPageSettings?.custom_colors?.button_bg;
      const font_color =
        dataShop.landingPageSettings?.custom_colors?.font_color;
      const primary_color =
        dataShop.landingPageSettings?.custom_colors?.primary_color;
      if (box_bg) body?.style.setProperty("--box_bg", box_bg);
      if (btn_font_color)
        body?.style.setProperty("--btn_font_color", btn_font_color);
      if (button_bg) body?.style.setProperty("--button_bg", button_bg);
      if (font_color) body?.style.setProperty("--font_color", font_color);
      if (primary_color)
        body?.style.setProperty("--primary_color", primary_color);
    }
  }, [product, dataShop]);

  const changeWordTranslation = (
    key: string,
    enWord: string,
    targetWord: string
  ) => {
    const nodes = Array.from(
      document.querySelectorAll(`[data-translation=${key}]`)
    );
    if (nodes.length > 0)
      nodes.forEach((node) => {
        // @ts-ignore
        if (node.tagName === "INPUT") node.placeholder = targetWord;
        else node.innerHTML = node.innerHTML.replace(enWord, targetWord);
      });
  };

  const translatePage = (lang: "en" | "fr" | "ar") => {
    document.querySelector("html")?.setAttribute("lang", lang);
    if (lang === "ar") {
      document.querySelector("html")?.setAttribute("dir", "rtl");
      Object.keys(arDict).forEach((key) => {
        // @ts-ignore
        changeWordTranslation(key, enDict[key], arDict[key]);
      });
    } else if (lang === "fr") {
      document.querySelector("html")?.setAttribute("dir", "ltr");
      Object.keys(frDict).forEach((key) => {
        // @ts-ignore
        changeWordTranslation(key, enDict[key], frDict[key]);
      });
    } else document.querySelector("html")?.setAttribute("dir", "ltr");
  };
  if (!dataShop || !product) return <div>Loading...</div>;
  if (isVariants)  return <LandingPagePreviewWithVariants GetLandingPageInfo={GetLandingPageInfo} dataShop={match} data={variantsData}/>
  return ( 
    <>
      <nav>
        <div>
          <img
            className="logo"
            src={dataShop?.logo}
            alt="logo"
            height="57"
            width="57"
          />
        </div>
        <div>
          {dataShop?.landingPageSettings?.header && (
            <p className="text_header">{dataShop.landingPageSettings.header}</p>
          )}
        </div>
      </nav>
      <div className="fixed show">
        <a href="#order-form">
          <button className="button buy-button wobble" data-translation="buy">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">
              <path d="M96 0C107.5 0 117.4 8.19 119.6 19.51L121.1 32H541.8C562.1 32 578.3 52.25 572.6 72.66L518.6 264.7C514.7 278.5 502.1 288 487.8 288H170.7L179.9 336H488C501.3 336 512 346.7 512 360C512 373.3 501.3 384 488 384H159.1C148.5 384 138.6 375.8 136.4 364.5L76.14 48H24C10.75 48 0 37.25 0 24C0 10.75 10.75 0 24 0H96zM128 464C128 437.5 149.5 416 176 416C202.5 416 224 437.5 224 464C224 490.5 202.5 512 176 512C149.5 512 128 490.5 128 464zM512 464C512 490.5 490.5 512 464 512C437.5 512 416 490.5 416 464C416 437.5 437.5 416 464 416C490.5 416 512 437.5 512 464z" />
            </svg>
            Buy now
          </button>
        </a>
      </div>
      <section className="main">
        <div className="product">
          <div className="product-details mobile">
            <h1 className="product-name">{product?.title}</h1>
            <div className="review">
              <ul className="stars">
                <li>
                  <div
                    className="star"
                    style={{
                      backgroundImage:
                        parseInt(product.rate) >= 1
                          ? "url(/assets/landing-page/svg/star.svg)"
                          : parseFloat(product.rate) > 0
                          ? "url(/assets/landing-page/svg/star-half.svg)"
                          : "",
                    }}
                  />
                </li>
                <li>
                  <div
                    className="star"
                    style={{
                      backgroundImage:
                        parseInt(product.rate) >= 2
                          ? "url(/assets/landing-page/svg/star.svg)"
                          : parseFloat(product.rate) > 1
                          ? "url(/assets/landing-page/svg/star-half.svg)"
                          : "",
                    }}
                  />
                </li>
                <li>
                  <div
                    className="star"
                    style={{
                      backgroundImage:
                        parseInt(product.rate) >= 3
                          ? "url(/assets/landing-page/svg/star.svg)"
                          : parseFloat(product.rate) > 2
                          ? "url(/assets/landing-page/svg/star-half.svg)"
                          : "",
                    }}
                  />
                </li>
                <li>
                  <div
                    className="star"
                    style={{
                      backgroundImage:
                        parseInt(product.rate) >= 4
                          ? "url(/assets/landing-page/svg/star.svg)"
                          : parseFloat(product.rate) > 3
                          ? "url(/assets/landing-page/svg/star-half.svg)"
                          : "",
                    }}
                  />
                </li>
                <li>
                  <div
                    className="star"
                    style={{
                      backgroundImage:
                        parseInt(product.rate) === 5
                          ? "url(/assets/landing-page/svg/star.svg)"
                          : parseFloat(product.rate) > 4
                          ? "url(/assets/landing-page/svg/star-half.svg)"
                          : "",
                    }}
                  />
                </li>
              </ul>
            </div>
            {("options" in product) && product?.options[0]?.reduced_price ? (
              <div className="product-price-container">
                <h3 className="product-price">
                  {Math.floor(product?.options[0]?.reduced_price)} {currency}
                </h3>
                <h3 className="product-price-crossed">
                  {Math.floor(product?.options[0]?.price)} {currency}
                </h3>
              </div>
            ) : (
              <>
                <h3 className="product-price-crossed hidden">0 {currency}</h3>
                <h3 className="product-price">
                  {("options" in product) && Math.floor(product?.options[0]?.price)} {currency}
                </h3>
              </>
            )}
          </div>
          <div className="slide-images">
            <div className="display-image">
              {product?.images?.map((img, index) => (
                <img
                  key={index}
                  src={img.url}
                  alt={img.order + ""}
                  className={index === activeImage ? "active" : ""}
                  width="480"
                  height="480"
                />
              ))}

              {product.images.length > 1 && (
                <>
                  <svg
                    width="34"
                    height="34"
                    viewBox="0 0 34 34"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="previous"
                    onClick={() => {
                      if (activeImage > 0)
                        setActiveImage((activeImage) => activeImage - 1);
                      else setActiveImage(product.images.length - 1);
                    }}
                  >
                    <path
                      d="M23.3381 5.63521C23.9815 6.31903 23.9487 7.39491 23.2648 8.03826L13.7393 17.0001L23.2648 25.9619C23.9487 26.6053 23.9815 27.6812 23.3381 28.365C22.6948 29.0488 21.6189 29.0816 20.9351 28.4383L11.1887 19.2686C9.87037 18.0283 9.87037 15.9719 11.1887 14.7316L20.9351 5.56195C21.6189 4.9186 22.6948 4.9514 23.3381 5.63521Z"
                      fill="#1A1A1A"
                    />
                  </svg>
                  <svg
                    className="next"
                    width="34"
                    height="34"
                    viewBox="0 0 34 34"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    onClick={() => {
                      if (activeImage < product.images.length - 1)
                        setActiveImage((activeImage) => activeImage + 1);
                      else setActiveImage(0);
                    }}
                  >
                    <path
                      d="M10.6619 28.3648C10.0185 27.681 10.0513 26.6051 10.7352 25.9617L20.2607 16.9999L10.7352 8.03805C10.0513 7.3947 10.0185 6.31882 10.6619 5.63501C11.3052 4.95119 12.3811 4.91839 13.0649 5.56174L22.8113 14.7314C24.1296 15.9717 24.1296 18.0281 22.8113 19.2684L13.0649 28.4381C12.3811 29.0814 11.3052 29.0486 10.6619 28.3648Z"
                      fill="#1A1A1A"
                    />
                  </svg>
                </>
              )}
            </div>
            <div className="previews">
              {product?.images.length > 1 &&
                product?.images?.map((img, index) => (
                  <img
                    key={img.url}
                    src={img.url}
                    alt={img.order + ""}
                    className="preview"
                    width="60"
                    height="60"
                    onClick={() => {
                      setActiveImage(index);
                    }}
                  />
                ))}
            </div>
          </div>
          <div className="product-details">
            <h1 className="product-name">{product?.title}</h1>
            <div className="review">
              <ul className="stars">
                <li>
                  <div
                    className="star"
                    style={{
                      backgroundImage:
                        parseInt(product.rate) >= 1
                          ? "url(/assets/landing-page/svg/star.svg)"
                          : parseFloat(product.rate) > 0
                          ? "url(/assets/landing-page/svg/star-half.svg)"
                          : "",
                    }}
                  />
                </li>
                <li>
                  <div
                    className="star"
                    style={{
                      backgroundImage:
                        parseInt(product.rate) >= 2
                          ? "url(/assets/landing-page/svg/star.svg)"
                          : parseFloat(product.rate) > 1
                          ? "url(/assets/landing-page/svg/star-half.svg)"
                          : "",
                    }}
                  />
                </li>
                <li>
                  <div
                    className="star"
                    style={{
                      backgroundImage:
                        parseInt(product.rate) >= 3
                          ? "url(/assets/landing-page/svg/star.svg)"
                          : parseFloat(product.rate) > 2
                          ? "url(/assets/landing-page/svg/star-half.svg)"
                          : "",
                    }}
                  />
                </li>
                <li>
                  <div
                    className="star"
                    style={{
                      backgroundImage:
                        parseInt(product.rate) >= 4
                          ? "url(/assets/landing-page/svg/star.svg)"
                          : parseFloat(product.rate) > 3
                          ? "url(/assets/landing-page/svg/star-half.svg)"
                          : "",
                    }}
                  />
                </li>
                <li>
                  <div
                    className="star"
                    style={{
                      backgroundImage:
                        parseInt(product.rate) === 5
                          ? "url(/assets/landing-page/svg/star.svg)"
                          : parseFloat(product.rate) > 4
                          ? "url(/assets/landing-page/svg/star-half.svg)"
                          : "",
                    }}
                  />
                </li>
              </ul>
              <span className="rate">{product?.rate}</span>
            </div>
            {("options" in product) && product?.options[0]?.reduced_price ? (
              <div className="product-price-container">
                <h3 className="product-price">
                  {Math.floor(product?.options[0]?.reduced_price)} {currency}
                </h3>
                <h3 className="product-price-crossed">
                  {Math.floor(product?.options[0]?.price)} {currency}
                </h3>
              </div>
            ) : (
              <>
                <h3 className="product-price-crossed hidden">0 {currency}</h3>
                <h3 className="product-price">
                  {("options" in product) && Math.floor(product?.options[0]?.price)} {currency}
                </h3>
              </>
            )}
            <form id="order-form" className="form">
              {("options" in product) && product?.options.length > 1 && (
                <div className="options">
                  <span data-translation="options">Options :</span>
                  {product.options.map((option, index) => (
                    <div key={option.id}>
                      {index === 0 ? (
                        <input
                          type="radio"
                          name="price-option"
                          id={option.id}
                          value={option.id}
                          checked
                          required
                        />
                      ) : (
                        <input
                          type="radio"
                          name="price-option"
                          id={option.id}
                          value={option.id}
                          required
                        />
                      )}
                      <label htmlFor={option.id}>
                        {option.name}
                        {option?.reduced_price ? (
                          <span className="price-crossed">
                            {Math.floor(option.price)} {currency}
                          </span>
                        ) : (
                          <span className="price">
                            {Math.floor(option.price)} {currency}
                          </span>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              )}
              <label
                className="quantity"
                data-translation="quantity"
                style={(("options" in product) && product?.options?.length > 1) ? { display: "none" } : {}}
              >
                Quantity:
                <input
                  type="number"
                  name="quantity"
                  value="1"
                  min="1"
                  required
                />
              </label>
              <input
                name="name"
                required
                placeholder="Full name"
                data-translation="placeholder_name"
              />
              <input
                type="tel"
                name="phone"
                placeholder="Phone number"
                data-translation="placeholder_phone"
                required
              />
              <label htmlFor="wilaya-1" data-translation="wilaya">
                <select name="wilaya" id="wilaya-1" required>
                  <option disabled selected data-translation="option-wilaya">
                    Select wilaya
                  </option>
                </select>
              </label>
              <label htmlFor="commune-1" data-translation="commune">
                <select name="commune" id="commune-1" required>
                  <option
                    value=""
                    disabled
                    selected
                    data-translation="option-wilaya"
                  >
                    Select commune
                  </option>
                </select>
              </label>
              <button
                className="button buy-button wobble disabled"
                data-translation="buy"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">
                  <path d="M96 0C107.5 0 117.4 8.19 119.6 19.51L121.1 32H541.8C562.1 32 578.3 52.25 572.6 72.66L518.6 264.7C514.7 278.5 502.1 288 487.8 288H170.7L179.9 336H488C501.3 336 512 346.7 512 360C512 373.3 501.3 384 488 384H159.1C148.5 384 138.6 375.8 136.4 364.5L76.14 48H24C10.75 48 0 37.25 0 24C0 10.75 10.75 0 24 0H96zM128 464C128 437.5 149.5 416 176 416C202.5 416 224 437.5 224 464C224 490.5 202.5 512 176 512C149.5 512 128 490.5 128 464zM512 464C512 490.5 490.5 512 464 512C437.5 512 416 490.5 416 464C416 437.5 437.5 416 464 416C490.5 416 512 437.5 512 464z" />
                </svg>
                Buy now
              </button>
              <div className="order-summary">
                <div className="head">
                  <span data-translation="order_summary">Order Summary</span>
                  <div className="accordion hide"> </div>
                </div>
                <div className="body">
                  <div className="subtotal">
                    <span data-translation="subtotal">Subtotal</span>
                    {("options" in product) && product?.options[0]?.reduced_price ? (
                      <span>
                        {Math.floor(product.options[0].reduced_price)}{" "}
                        {currency}
                      </span>
                    ) : (
                      <span>
                        {("options" in product) && Math.floor(product?.options[0]?.price)} {currency}
                      </span>
                    )}
                  </div>
                  <div className="shipping">
                    <span data-translation="shipping">Shipping</span>
                    <span></span>
                  </div>
                  <div className="total_amount">
                    <span data-translation="total_amount">Total amount</span>
                    {("options" in product) &&  product?.options[0]?.reduced_price ? (
                      <span>
                        {Math.floor(product.options[0].reduced_price)}{" "}
                        {currency}
                      </span>
                    ) : (
                      <span>
                        {("options" in product) && Math.floor(product.options[0].price)} {currency}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        {product?.description && (
          <div className="section">
            <h2 className="title" data-translation="description">
              Description
            </h2>
            <div dangerouslySetInnerHTML={{ __html: product.description }} />
          </div>
        )}

        {product?.template_details[0]?.custom_sections?.map((section) => (
          <div className="section">
            <h2 className="title">{section.title}</h2>
            <div dangerouslySetInnerHTML={{ __html: section.content }} />
          </div>
        ))}
      </section>
      <footer>
        <div className="badge">
          <a href="https://maystro-delivery.com" target="_blank" rel="noopener">
            <img
              src={`/assets/landing-page/svg/badge-footer-${dataShop.theme_template}.svg`}
              alt="Powered by Maystro Delivery"
              loading="lazy"
              width="210"
              height="45"
            />
          </a>
        </div>
        <div className="social">
          {dataShop?.landingPageSettings?.social_media_url?.facebook && (
            <div className="facebook">
              <a
                href={dataShop?.landingPageSettings?.social_media_url?.facebook}
                target="_blank"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M20 10C20 4.47758 15.5224 0 10 0C4.47758 0 0 4.47758 0 10C0 14.9908 3.65634 19.1281 8.43765 19.8791V12.8913H5.8979V10H8.43765V7.79635C8.43765 5.29035 9.9311 3.90517 12.2149 3.90517C13.3089 3.90517 14.4537 4.10064 14.4537 4.10064V6.56165H13.1922C11.9505 6.56165 11.5623 7.33229 11.5623 8.124V9.99992H14.3355L13.8925 12.8912H11.5623V19.879C16.3436 19.1294 19.9999 14.9922 19.9999 9.99992L20 10Z"
                  />
                </svg>
              </a>
            </div>
          )}
          {dataShop?.landingPageSettings?.social_media_url?.instagram && (
            <div className="instagram">
              <a
                href={
                  dataShop?.landingPageSettings?.social_media_url?.instagram
                }
                target="_blank"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M10.0088 4.90436C9.83291 4.90436 9.65895 4.91333 9.48756 4.93089C9.31616 4.94844 9.14714 4.97446 8.981 5.00861C8.81486 5.04276 8.65167 5.08504 8.49175 5.13496L8.02235 5.30731L7.57545 5.52311L7.15373 5.77972L6.75968 6.07445L6.39607 6.40474L6.06546 6.76803L5.77041 7.16162L5.51348 7.58296L5.29756 8.02935C5.23265 8.18204 5.17492 8.33857 5.12495 8.49836C5.07497 8.65816 5.03268 8.82129 4.99847 8.98723C4.96425 9.15318 4.93824 9.32201 4.92062 9.49328C4.903 9.66454 4.89416 9.8383 4.89416 10.014C4.89416 10.1897 4.90313 10.3634 4.92062 10.5347C4.93801 10.7046 4.96399 10.8735 4.99847 11.0408C5.03268 11.2074 5.07497 11.3699 5.12495 11.5297C5.17492 11.6895 5.23259 11.8459 5.29756 11.9987L5.51348 12.4451L5.77041 12.8664L6.06546 13.26L6.39607 13.6233L6.75968 13.9536L7.15373 14.2483L7.57545 14.5049L8.02235 14.7207L8.49175 14.8931L8.981 15.0194C9.14844 15.0539 9.3175 15.0798 9.48756 15.0972C9.66074 15.1149 9.8347 15.1237 10.0088 15.1237C10.1847 15.1237 10.3586 15.1147 10.5299 15.0972C10.7 15.0798 10.8691 15.0539 11.0365 15.0194L11.5258 14.8931L11.9952 14.7207L12.4421 14.5049L12.8638 14.2483L13.2579 13.9536L13.6216 13.6233L13.9522 13.26L14.2472 12.8664L14.5042 12.4451L14.7201 11.9987L14.8927 11.5297C14.9427 11.3699 14.9849 11.2068 15.0192 11.0408L15.097 10.5346C15.1146 10.3634 15.1235 10.1896 15.1235 10.0139C15.1235 9.83824 15.1146 9.66448 15.097 9.49321C15.0796 9.32332 15.0536 9.15443 15.0192 8.98717C14.9849 8.82058 14.9427 8.65816 14.8927 8.4983L14.7201 8.02929L14.5042 7.5829L14.2472 7.16156L13.9522 6.76796L13.6216 6.40468L13.2579 6.07438L12.8638 5.77965L12.4421 5.52304L11.9952 5.30725L11.5258 5.13489L11.0365 5.00854L10.5299 4.93082C10.3568 4.91314 10.1828 4.90428 10.0088 4.9043V4.90436ZM10.0088 6.66801C10.1251 6.66801 10.2398 6.67378 10.3529 6.68518L10.686 6.73561L11.0079 6.81762L11.316 6.92956L11.6091 7.06987L11.8846 7.23678L12.1421 7.42881L12.3796 7.64422L12.5952 7.88141L12.7874 8.13873L12.9545 8.41462L13.0948 8.70743C13.137 8.80764 13.1744 8.91035 13.2069 9.01536C13.2395 9.12038 13.2669 9.2277 13.289 9.337C13.3111 9.44631 13.3274 9.55754 13.3394 9.6705L13.3566 10.0143C13.3566 10.1305 13.3508 10.2452 13.3394 10.3582C13.328 10.4712 13.3112 10.5824 13.289 10.6917C13.2667 10.8009 13.2393 10.9082 13.2069 11.0133L13.0948 11.3213L12.9545 11.6141L12.7874 11.8899L12.5952 12.1473L12.3796 12.3845L12.1421 12.5999L11.8846 12.7919L11.6091 12.9588L11.316 13.0991L11.0079 13.2111L10.686 13.2931L10.3529 13.3434C10.2385 13.3549 10.1236 13.3606 10.0087 13.3606C9.89377 13.3606 9.7789 13.3549 9.66452 13.3434L9.33135 13.2931C9.22198 13.2709 9.11459 13.2435 9.00945 13.2111L8.70126 13.0991L8.40826 12.9588L8.13275 12.7919L7.87518 12.5999L7.63773 12.3845L7.42213 12.1473L7.22991 11.8899L7.06281 11.6141L6.92249 11.3213L6.81036 11.0133L6.72835 10.6917L6.67793 10.3582C6.66646 10.2439 6.66073 10.1292 6.66076 10.0143C6.66073 9.89949 6.66646 9.78474 6.67793 9.6705C6.68927 9.55754 6.70612 9.44625 6.72835 9.337C6.75036 9.22852 6.77776 9.12119 6.81043 9.01542L6.92255 8.70749L7.06287 8.41468L7.22997 8.13879L7.42219 7.88148L7.63779 7.64428L7.87524 7.42887L8.13281 7.23684L8.40832 7.06994L8.70133 6.92962C8.80218 6.88727 8.90505 6.84991 9.00958 6.81768L9.33148 6.73567C9.44085 6.7135 9.55221 6.69659 9.66465 6.68525C9.77903 6.67379 9.8939 6.66806 10.0088 6.66808L10.0088 6.66801Z" />
                  <path d="M15.3309 3.45117C15.2903 3.45118 15.2497 3.45323 15.2093 3.45732C15.1689 3.46141 15.1287 3.46752 15.0889 3.47565C15.0491 3.48376 15.0097 3.49388 14.9709 3.50595C14.9321 3.51804 14.894 3.53206 14.8566 3.54799C14.8192 3.56392 14.7826 3.58174 14.7469 3.60136C14.7113 3.62094 14.6767 3.6423 14.6431 3.66537C14.6096 3.68845 14.5773 3.71322 14.5462 3.73956C14.5151 3.76588 14.4854 3.79374 14.4571 3.82305C14.4288 3.85237 14.402 3.8831 14.3768 3.91512C14.3516 3.94714 14.3279 3.98042 14.306 4.01481C14.2841 4.04919 14.2639 4.08464 14.2454 4.12105C14.227 4.15748 14.2104 4.19481 14.1957 4.23292C14.1809 4.27101 14.1681 4.30982 14.1573 4.34921C14.1464 4.38859 14.1375 4.42849 14.1306 4.46877C14.1237 4.50903 14.1188 4.54962 14.116 4.59038C14.1139 4.61925 14.1129 4.64818 14.1129 4.67713C14.1129 4.71802 14.1149 4.75888 14.119 4.79957C14.123 4.84024 14.1291 4.88068 14.1372 4.92073C14.1452 4.96079 14.1553 5.00041 14.1673 5.03946C14.1793 5.07851 14.1932 5.11694 14.209 5.1546C14.2249 5.19226 14.2426 5.22911 14.262 5.26499C14.2815 5.30087 14.3027 5.33575 14.3257 5.36949C14.3486 5.40322 14.3732 5.43576 14.3993 5.46701C14.4255 5.49829 14.4532 5.52822 14.4823 5.55671C14.5115 5.58518 14.542 5.61217 14.5738 5.63757C14.6056 5.66299 14.6387 5.68679 14.6729 5.70888C14.7071 5.73098 14.7423 5.75133 14.7785 5.76988C14.8147 5.78844 14.8518 5.80516 14.8896 5.81998C14.9275 5.83479 14.966 5.84769 15.0051 5.85862C15.0443 5.86954 15.0839 5.87847 15.1239 5.8854C15.1639 5.89236 15.2043 5.8973 15.2448 5.9002C15.2735 5.90225 15.3022 5.90328 15.331 5.90328C15.3716 5.90329 15.4122 5.90126 15.4526 5.89719C15.493 5.89311 15.5332 5.88699 15.573 5.87887C15.6127 5.87075 15.6521 5.86064 15.6909 5.84856C15.7297 5.83648 15.7679 5.82245 15.8053 5.80653C15.8427 5.79061 15.8793 5.77282 15.9149 5.75322C15.9505 5.73362 15.9852 5.71224 16.0187 5.68915C16.0522 5.66608 16.0846 5.64134 16.1156 5.61502C16.1467 5.58868 16.1764 5.5608 16.2047 5.53147C16.233 5.50215 16.2598 5.47142 16.2851 5.4394C16.3103 5.40737 16.3339 5.3741 16.3559 5.3397C16.3778 5.30531 16.398 5.26983 16.4164 5.2334C16.4348 5.19697 16.4515 5.15964 16.4662 5.12153C16.4809 5.08344 16.4937 5.04463 16.5046 5.00524C16.5154 4.96586 16.5243 4.92596 16.5312 4.88568C16.5381 4.84542 16.543 4.80483 16.5459 4.76408C16.5479 4.7352 16.549 4.70627 16.549 4.67732C16.549 4.63645 16.5469 4.59561 16.5429 4.55494C16.5388 4.51428 16.5327 4.47384 16.5247 4.43378C16.5166 4.39373 16.5066 4.3541 16.4946 4.31506C16.4826 4.276 16.4686 4.23757 16.4528 4.19992C16.437 4.16225 16.4193 4.12541 16.3998 4.08952C16.3803 4.05364 16.3591 4.01877 16.3362 3.98502C16.3132 3.95128 16.2886 3.91871 16.2624 3.88744C16.2363 3.85617 16.2086 3.82623 16.1795 3.79774C16.1504 3.76927 16.1198 3.74228 16.088 3.71688C16.0562 3.69148 16.0232 3.6677 15.989 3.64563C15.9548 3.62354 15.9195 3.60318 15.8833 3.58463C15.8471 3.56608 15.8101 3.54936 15.7722 3.53453C15.7344 3.51972 15.6958 3.50683 15.6567 3.49589C15.6176 3.48496 15.5779 3.476 15.5379 3.46905C15.4979 3.46211 15.4576 3.45719 15.4171 3.45431C15.3884 3.45226 15.3596 3.45124 15.3309 3.45124V3.45117Z" />
                  <path d="M9.0389 0.0177645C8.48263 0.0168674 7.9862 0.0191737 7.54026 0.0254528L6.34467 0.0574889C5.99048 0.0728662 5.67755 0.0929193 5.39666 0.118869C5.25621 0.131811 5.12371 0.146292 4.99813 0.162246C4.87862 0.177308 4.75944 0.194847 4.64067 0.21485C4.52758 0.234071 4.42026 0.25483 4.31736 0.277383C4.21446 0.299937 4.11617 0.324285 4.02135 0.350426C3.92864 0.375989 3.83667 0.404106 3.74552 0.434746C3.6562 0.46486 3.56906 0.496768 3.48301 0.530854C3.31091 0.599027 3.14304 0.675528 2.97018 0.761256C2.83716 0.827251 2.71427 0.894078 2.59753 0.964814C2.48079 1.03555 2.37021 1.11026 2.26192 1.19163C2.15041 1.27584 2.04293 1.36525 1.93983 1.45958C1.83206 1.55774 1.72199 1.6657 1.60762 1.78635C1.4639 1.93711 1.33903 2.07768 1.22818 2.21563C1.17276 2.28457 1.12086 2.35275 1.07197 2.4213C1.02363 2.48887 0.97741 2.55793 0.933387 2.62838C0.889818 2.69822 0.848556 2.76934 0.809087 2.84251C0.769003 2.91694 0.73112 2.99254 0.695487 3.0692C0.622573 3.22579 0.554528 3.39443 0.486612 3.58268C0.436226 3.72184 0.391109 3.86286 0.351355 4.00542C0.310798 4.15112 0.274725 4.30355 0.242561 4.46841C0.210396 4.63327 0.182333 4.81055 0.157665 5.00662C0.131083 5.22153 0.109847 5.43707 0.0939771 5.65304C0.0760369 5.89049 0.0611721 6.15178 0.0489984 6.4426L0.020166 7.40958L0.00466051 8.58832L0.000175476 10.0131L0.0133103 12.085L0.0543164 13.6548L0.085904 14.2679L0.125373 14.776C0.138025 14.913 0.153855 15.0496 0.17285 15.1858C0.19015 15.3068 0.20873 15.4126 0.228721 15.5043C0.269283 15.6905 0.317247 15.8751 0.372499 16.0576C0.425402 16.2323 0.485901 16.4046 0.553823 16.574C0.620587 16.74 0.693629 16.9 0.773078 17.054C0.852528 17.2081 0.938384 17.3561 1.03065 17.4983C1.12291 17.6404 1.22158 17.7768 1.32692 17.9073C1.43213 18.0378 1.54405 18.1628 1.66221 18.2817C1.78055 18.4009 1.90536 18.5143 2.0369 18.6221C2.17 18.7311 2.30835 18.8335 2.45145 18.929C2.58427 19.0178 2.71568 19.0991 2.84806 19.1737C2.98043 19.2484 3.11396 19.3161 3.2512 19.3777C3.38844 19.4393 3.52914 19.4947 3.67613 19.5445C3.82311 19.5944 3.97624 19.6387 4.13809 19.6781C4.29993 19.7174 4.47126 19.7519 4.65259 19.782C4.83391 19.8122 5.02773 19.838 5.23481 19.8602C5.44189 19.8824 5.66281 19.9008 5.9002 19.9162C6.13759 19.9316 6.39131 19.9439 6.66394 19.9538C7.08509 19.969 7.7226 19.9779 8.45777 19.9813L10.8377 19.9765L13.0914 19.9449L13.9483 19.9208L14.5066 19.8921C14.6264 19.8824 14.7436 19.8708 14.8568 19.8573C14.9699 19.8438 15.0816 19.8284 15.1902 19.8108C15.2989 19.7933 15.4051 19.7736 15.5095 19.7519C15.6127 19.7304 15.7153 19.7064 15.8173 19.6799C15.9183 19.6543 16.0178 19.6251 16.1164 19.5942C16.2149 19.5632 16.3124 19.5298 16.4094 19.4939C16.5069 19.4578 16.6035 19.4193 16.6991 19.3785C16.7954 19.3375 16.8916 19.2937 16.9881 19.2472C17.1363 19.1758 17.2556 19.1163 17.3603 19.0582C17.4589 19.0041 17.5538 18.9437 17.6444 18.8772C17.7339 18.8113 17.8228 18.7364 17.9253 18.6419C18.0278 18.5475 18.144 18.4337 18.2879 18.2898C18.4143 18.1635 18.5209 18.0536 18.6128 17.9537C18.7048 17.8539 18.7822 17.764 18.8502 17.6773C18.9149 17.5951 18.9752 17.5095 19.0308 17.4209C19.0851 17.3344 19.1351 17.2446 19.186 17.145C19.2626 16.995 19.3308 16.8562 19.3919 16.7222C19.4512 16.5931 19.5053 16.4617 19.5542 16.3283C19.6024 16.196 19.6441 16.0614 19.6792 15.925C19.7154 15.7846 19.7465 15.6365 19.7734 15.474C19.8003 15.3115 19.8231 15.1347 19.8428 14.9372C19.8626 14.7398 19.8793 14.5215 19.8941 14.2763L19.9332 13.4532L19.9667 12.4295L19.9907 11.289L19.9999 10.0965L19.9785 7.75435L19.9499 6.7053L19.9108 5.80457L19.8621 5.10234C19.8444 4.90692 19.8253 4.75302 19.8051 4.64896C19.7827 4.53399 19.7576 4.41955 19.73 4.30573C19.7031 4.19497 19.6735 4.08489 19.6412 3.97557C19.6098 3.86906 19.5757 3.76337 19.5389 3.6586C19.5025 3.55512 19.4639 3.45383 19.4235 3.35471C19.3832 3.25559 19.3395 3.15877 19.294 3.06401C19.2485 2.96925 19.2008 2.87679 19.1508 2.78645C19.1007 2.69611 19.0486 2.60801 18.9941 2.52209C18.9395 2.43617 18.8828 2.35243 18.8236 2.27093C18.7648 2.18969 18.7035 2.1103 18.6397 2.0329C18.5763 1.95591 18.5104 1.88096 18.4423 1.80813C18.3742 1.7354 18.3038 1.66484 18.2313 1.59657C18.1587 1.52827 18.0839 1.46214 18.0068 1.3982C17.9293 1.33404 17.8499 1.27231 17.7687 1.21309C17.6866 1.15327 17.6026 1.09596 17.517 1.04125C17.4302 0.985764 17.3418 0.932859 17.2519 0.88261C17.1604 0.831519 17.0674 0.78306 16.9732 0.737294C16.8167 0.66124 16.672 0.593773 16.53 0.53393C16.3903 0.474539 16.248 0.42179 16.1034 0.375863C15.9579 0.330052 15.8065 0.290776 15.6399 0.256881C15.4734 0.222987 15.2917 0.194731 15.0861 0.170704C14.8805 0.146677 14.6509 0.127264 14.3884 0.111118C14.1258 0.0949715 13.8305 0.0822842 13.4933 0.0720326L12.3473 0.0464041L10.8969 0.029297L9.03954 0.0178916L9.0389 0.0177645ZM10.0035 1.77988L12.6819 1.79513L13.6923 1.8146C13.9706 1.82242 14.1829 1.83158 14.3074 1.84215C14.4695 1.8558 14.6239 1.87291 14.7716 1.89341C14.9134 1.91298 15.0544 1.93732 15.1945 1.96639C15.3291 1.99458 15.4574 2.02662 15.5802 2.06276C15.7031 2.09889 15.8204 2.13939 15.9329 2.18417C16.0455 2.22896 16.1532 2.27823 16.2568 2.33218C16.3595 2.38572 16.4594 2.44464 16.556 2.5087C16.6521 2.57245 16.7447 2.64113 16.8347 2.71514C16.9247 2.78914 17.0119 2.86846 17.097 2.95329C17.1869 3.0428 17.2698 3.13398 17.3462 3.22804C17.4224 3.32151 17.4929 3.41948 17.5572 3.52142C17.6224 3.62481 17.681 3.73212 17.7329 3.84274C17.786 3.95551 17.8336 4.07469 17.8764 4.20136C17.9192 4.32803 17.9574 4.46264 17.9911 4.60648C18.0265 4.75898 18.0561 4.91276 18.0799 5.06748C18.1056 5.23151 18.1275 5.40643 18.1461 5.59371C18.1646 5.78099 18.18 5.9807 18.1927 6.19439C18.2028 6.36578 18.2117 6.59195 18.2192 6.85907L18.2379 7.76986L18.2523 10.0008L18.236 12.2297L18.2163 13.1379L18.1894 13.7984C18.1707 14.1077 18.1509 14.3677 18.1274 14.594C18.1155 14.7071 18.1027 14.8118 18.0885 14.9101C18.0751 15.0029 18.0594 15.0953 18.0411 15.1872C18.0245 15.2711 18.0051 15.3545 17.9831 15.4371C17.9621 15.516 17.9384 15.5941 17.9121 15.6714C17.8856 15.7491 17.8568 15.8259 17.8256 15.9018C17.7927 15.982 17.758 16.0614 17.7215 16.14C17.689 16.2101 17.6549 16.2784 17.6187 16.3448C17.583 16.4106 17.5451 16.4751 17.505 16.5383C17.4652 16.6009 17.4236 16.6617 17.38 16.7206C17.3367 16.7793 17.2913 16.8364 17.2438 16.8918C17.1965 16.947 17.1472 17.0004 17.096 17.052C17.0447 17.1036 16.9915 17.1534 16.9364 17.2014C16.8813 17.2495 16.8242 17.2956 16.765 17.3401C16.7059 17.3845 16.6447 17.4272 16.5815 17.4681C16.5182 17.509 16.453 17.5482 16.3857 17.5856C16.3183 17.623 16.2489 17.6588 16.1774 17.6928C16.1048 17.7273 16.0311 17.7597 15.9565 17.7898C15.8793 17.8209 15.8013 17.8498 15.7226 17.8766C15.5624 17.9312 15.3935 17.9791 15.2156 18.0204C15.0302 18.0633 14.8431 18.0983 14.6548 18.1254C14.5824 18.136 14.416 18.1459 14.1777 18.1551L13.2694 18.1804L10.6943 18.2138L7.99908 18.218L6.9406 18.2068L6.2533 18.1854L5.74649 18.1497C5.58913 18.1365 5.44279 18.1217 5.30567 18.105C5.16856 18.0883 5.04086 18.0697 4.92073 18.0486C4.80688 18.0289 4.69373 18.0054 4.58147 17.9782C4.4791 17.9533 4.37778 17.9242 4.27776 17.8911C4.18133 17.8591 4.08913 17.8239 3.99943 17.7851C3.90973 17.7463 3.82259 17.7039 3.73629 17.6575C3.64998 17.6111 3.56458 17.5607 3.47821 17.5058C3.41305 17.4644 3.35006 17.4225 3.28945 17.3781C3.22884 17.3337 3.17028 17.2894 3.11389 17.2431C3.05751 17.1967 3.00337 17.1489 2.95115 17.0997C2.89928 17.0507 2.84918 16.9999 2.80096 16.9473C2.75291 16.8949 2.70697 16.8409 2.66289 16.7851C2.61864 16.729 2.57647 16.6713 2.53647 16.6121C2.49588 16.552 2.45748 16.4904 2.42134 16.4275C2.38481 16.3641 2.35009 16.2985 2.31709 16.2308C2.28354 16.1619 2.25223 16.0919 2.22322 16.0209C2.19375 15.9486 2.16582 15.874 2.13955 15.797C2.08701 15.6429 2.04087 15.4789 2.00077 15.304C1.96066 15.1291 1.92644 14.9432 1.89748 14.7453C1.86852 14.5473 1.8452 14.3374 1.82662 14.1143C1.81291 13.9499 1.80201 13.694 1.79368 13.371L1.77626 12.2226L1.78266 9.29477L1.83309 6.49713L1.87153 5.51144L1.89332 5.18563C1.90088 5.09862 1.90869 5.03417 1.9167 4.99547C1.9431 4.86816 1.96969 4.74969 1.99718 4.63878C2.02285 4.53415 2.05142 4.43025 2.08284 4.32719C2.1127 4.22993 2.14416 4.13882 2.1778 4.05252C2.21023 3.96891 2.24638 3.8868 2.28614 3.80642C2.32497 3.72825 2.36668 3.65341 2.41198 3.58056C2.45728 3.50771 2.50617 3.43704 2.55948 3.36675C2.61278 3.29646 2.67019 3.22707 2.73266 3.15672C2.79513 3.08637 2.8626 3.01532 2.93577 2.94208C3.02105 2.85632 3.10984 2.77413 3.20193 2.69572C3.28888 2.62175 3.37955 2.55227 3.47359 2.48755C3.56605 2.42348 3.66088 2.36646 3.75961 2.31405C3.85835 2.26164 3.96099 2.21429 4.06895 2.17168C4.17691 2.12907 4.29032 2.09095 4.41046 2.05635C4.53667 2.02095 4.66428 1.99074 4.79297 1.96581C4.92829 1.93929 5.072 1.91616 5.22545 1.89597C5.37891 1.87579 5.54216 1.85836 5.7167 1.84324C5.83843 1.83274 6.04821 1.82338 6.32442 1.8155L7.32971 1.79564L10.004 1.7793L10.0035 1.77988Z" />
                </svg>
              </a>
            </div>
          )}
          {dataShop?.landingPageSettings?.social_media_url?.tiktok && (
            <div className="tiktok">
              <a
                href={dataShop?.landingPageSettings?.social_media_url?.tiktok}
                target="_blank"
              >
                <svg
                  width="18"
                  height="20"
                  viewBox="0 0 18 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M14.2332 3.83254C13.5041 3.00231 13.1022 1.93502 13.1026 0.830078H12.2209C12.3348 1.44099 12.572 2.02229 12.9179 2.53851C13.2639 3.05472 13.7115 3.49502 14.2332 3.83254ZM6.07564 10.3292C5.45732 10.3324 4.85779 10.5421 4.37238 10.9251C3.88696 11.3082 3.54353 11.8425 3.39666 12.4431C3.2498 13.0437 3.30794 13.6762 3.56185 14.24C3.81575 14.8038 4.25085 15.2666 4.79796 15.5547C4.49883 15.1417 4.3197 14.654 4.28035 14.1455C4.24101 13.6371 4.34298 13.1277 4.57501 12.6736C4.80704 12.2194 5.16009 11.8383 5.59515 11.5723C6.03021 11.3062 6.53035 11.1656 7.04032 11.166C7.31755 11.1696 7.59285 11.2128 7.85791 11.2942V7.93367C7.58723 7.89309 7.31401 7.87175 7.04032 7.86981H6.89332V10.425C6.62686 10.3535 6.3514 10.3213 6.07564 10.3292Z" />
                  <path d="M16.7309 5.32031V7.87559C15.0858 7.87239 13.4834 7.35165 12.1507 6.38714V13.101C12.1436 16.4512 9.42578 19.1634 6.07564 19.1634C4.83141 19.1656 3.61715 18.7817 2.60035 18.0646C3.42948 18.9564 4.50797 19.5779 5.69533 19.848C6.88269 20.1182 8.12388 20.0245 9.25723 19.5791C10.3906 19.1338 11.3636 18.3575 12.0494 17.3514C12.7353 16.3452 13.1023 15.1558 13.1025 13.9381V7.24321C14.4397 8.20136 16.0441 8.7153 17.6892 8.71246V5.42229C17.3671 5.42133 17.046 5.38716 16.7309 5.32031Z" />
                  <path d="M12.1507 13.1016V6.38773C13.4875 7.34675 15.0921 7.86079 16.7373 7.85699V5.3018C15.7654 5.09654 14.8865 4.58087 14.2332 3.83254C13.7115 3.49502 13.2639 3.05472 12.9179 2.53851C12.572 2.02229 12.3348 1.44099 12.2209 0.830078H9.80636V14.0537C9.78285 14.6293 9.58062 15.1832 9.22778 15.6386C8.87493 16.094 8.38899 16.4281 7.8375 16.5946C7.286 16.7611 6.69634 16.7517 6.15046 16.5676C5.60457 16.3836 5.12959 16.034 4.7915 15.5676C4.2443 15.2795 3.80911 14.8168 3.55514 14.253C3.30117 13.6891 3.243 13.0566 3.38987 12.4559C3.53674 11.8552 3.88023 11.3208 4.36571 10.9378C4.85119 10.5548 5.4508 10.345 6.06918 10.342C6.34652 10.3444 6.622 10.3875 6.88686 10.4698V7.91448C5.71184 7.9344 4.568 8.29578 3.59481 8.95455C2.62163 9.61331 1.86113 10.541 1.40606 11.6245C0.95099 12.708 0.821004 13.9005 1.03195 15.0566C1.2429 16.2127 1.78566 17.2825 2.59407 18.1354C3.62086 18.8289 4.83685 19.1881 6.07564 19.164C9.42578 19.164 12.1436 16.4518 12.1507 13.1016V13.1016Z" />
                </svg>
              </a>
            </div>
          )}
        </div>
      </footer>
    </>
  );
};

function mapState(state: any) {
  const { dataShop } = state.shop;
  return {
    dataShop,
  };
}

const actionCreators = {
  GetLandingPageInfo: shopActions.GetLandingPageInfo,
};

export default connect(mapState, actionCreators)(LandingPagePreview);